import { FormControlLabel, Grid, MenuItem, Paper, Switch, TextField } from "@mui/material";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSiteService } from "./siteService";
import { useHistory } from "react-router-dom";
import { useTenantService, SiteLifecycleStatus, toSiteLifecycleStatus, siteLifecycleStatusToNumber } from "../tenants/tenantService";
import { useEffect, useState } from "react";

import ReportCronEditor from "./cron-definition/ReportCronEditor";
import { isReportCronValid } from "./cron-definition/reportCron";
import { MarkdownEditor } from "../shared/MarkdownEditor";
import { FormContainer, FormHeader, FormSection } from "../shared/FormLayout";

type Props = {
  site: any;
};

export const EditSiteForm: React.FC<Props> = ({ site }) => {

  const siteService = useSiteService();
  const history = useHistory();

  const tenantService = useTenantService();

  const [tenants, setTenants] = useState<any>(null);

  useEffect(() => {
    tenantService.getAll().then(setTenants);
  }, []);

  const [notes, setNotes] = useState(site?.notes ?? '');

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Navn er påkrevd"),
    siteType: Yup.string().required("Type er påkrevd"),
    phoneNumber: Yup.string().required("Telefon er påkrevd"),
    tenantId: Yup.string().required("Tenant er påkrevd"),
    reportCron: Yup.string()
      .required('Velg når testen skal kjøres')
      .test('report-cron', 'Ugyldig cron-uttrykk', (value) => isReportCronValid(value ?? '')),
  });
  const formOptions = {
    defaultValues: {
      name: site?.name ?? "",
      siteType: site?.siteType ?? "",
      lifecycleStatus: toSiteLifecycleStatus(site?.lifecycleStatus),
      tenantId: site?.tenantId ?? "",
      phoneNumber: site?.phoneNumber ?? "",
      address: site?.address ?? "",
      reportCron: site?.reportCron ?? '',
      customerNumber: site?.customerNumber ?? "",
      organizationNumber: site?.organizationNumber ?? "",
      subscriptionPriceSite: site?.subscriptionPrices?.site ?? "",
      subscriptionPriceEntity: site?.subscriptionPrices?.entity ?? "",
      hidden: site?.hidden ?? false,
    },
    resolver: yupResolver(validationSchema),
  };

  const { register, handleSubmit, setError, formState, setValue } =
    useForm(formOptions);
  const { errors, isSubmitting } = formState;

  useEffect(() => {
    register('reportCron');
  }, [register]);

  const onSubmit = async (values: any) => {
    const sitePriceValue = values.subscriptionPriceSite === "" ? null : Number(values.subscriptionPriceSite);
    const entityPriceValue = values.subscriptionPriceEntity === "" ? null : Number(values.subscriptionPriceEntity);

    // Hvis begge er null/tomme, sett hele subscriptionPrices til null (bruk tenant default)
    const subscriptionPrices = (sitePriceValue === null && entityPriceValue === null)
      ? null
      : { site: sitePriceValue ?? 0, entity: entityPriceValue ?? 0 };

    const { subscriptionPriceSite, subscriptionPriceEntity, lifecycleStatus, ...rest } = values;
    const formData = { ...rest, lifecycleStatus: siteLifecycleStatusToNumber(lifecycleStatus), notes, subscriptionPrices };
    if(!site) {
      await siteService.post({...formData, createdOn: new Date().toUTCString()});
    } else {
      await siteService.put(site.id, {...site, ...formData});
    }
    history.push('/sites')
  };

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <FormHeader section="Sites" title={site?.name || "Ny site"} isSubmitting={isSubmitting}>
        <FormControlLabel
          control={
            <Switch
              defaultChecked={site?.hidden ?? false}
              {...register("hidden")}
              onChange={(_, v) => setValue("hidden", v)}
            />
          }
          label="Skjult"
        />
      </FormHeader>

      <FormSection title="Grunninfo">
        <Grid item xs={12} md={6}>
          <TextField
            label="Navn"
            size="small"
            fullWidth
            {...register("name")}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            {...register("siteType")}
            size="small"
            fullWidth
            select
            onChange={(e) => setValue("siteType", e.target.value)}
            label="Type"
            defaultValue={formOptions.defaultValues.siteType}
            error={!!errors.siteType}
            helperText={errors.siteType?.message}
          >
            <MenuItem value={"Restaurant"}>Restaurant</MenuItem>
            <MenuItem value={"AmusementPark"}>Amusement Park</MenuItem>
            <MenuItem value={"Building"}>Building</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            {...register("lifecycleStatus")}
            size="small"
            fullWidth
            select
            onChange={(e) => setValue("lifecycleStatus", e.target.value)}
            label="Status"
            defaultValue={formOptions.defaultValues.lifecycleStatus}
          >
            {Object.values(SiteLifecycleStatus).map((status) => (
              <MenuItem key={status} value={status}>{status}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            {...register("tenantId")}
            size="small"
            fullWidth
            select
            onChange={(e) => setValue("tenantId", e.target.value)}
            label="Tenant"
            defaultValue={formOptions.defaultValues.tenantId}
            error={!!errors.tenantId}
            helperText={errors.tenantId?.message}
          >
            {tenants?.map((tenant: any) => <MenuItem value={tenant.id} key={tenant.id}>{tenant.name}</MenuItem>) ?? []}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Telefon"
            size="small"
            fullWidth
            {...register("phoneNumber")}
            error={!!errors.phoneNumber}
            helperText={errors.phoneNumber?.message}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Adresse"
            size="small"
            fullWidth
            {...register("address")}
            error={!!errors.address}
            helperText={errors.address?.message}
          />
        </Grid>
      </FormSection>

      <FormSection title="Kunde og pris">
        <Grid item xs={12} sm={6} md={3}>
          <TextField label="Kundenummer" size="small" fullWidth {...register("customerNumber")} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label="Organisasjonsnummer" size="small" fullWidth {...register("organizationNumber")} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Sitepris (NOK)"
            size="small"
            fullWidth
            helperText="Tom = tenant-standard"
            {...register("subscriptionPriceSite")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Entitypris (NOK)"
            size="small"
            fullWidth
            helperText="Tom = tenant-standard"
            {...register("subscriptionPriceEntity")}
          />
        </Grid>
      </FormSection>

      <ReportCronEditor
        value={site?.reportCron}
        onChange={(v) => setValue('reportCron', v, { shouldValidate: formState.isSubmitted })}
        error={errors.reportCron?.message}
      />

      <Paper variant="outlined">
        <MarkdownEditor
          label="Notat"
          value={notes}
          onChange={setNotes}
        />
      </Paper>
    </FormContainer>
  );
};
